import React, { useEffect, useState } from 'react'
import { Download, X, Share, MoreVertical } from 'lucide-react'

import { C } from './theme'

function isIos() {
  const ua = navigator.userAgent || ''
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return true
  // iPadOS 13+ reporta como Mac. Detectar via platform + touch.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true
  return false
}
function isAndroid() {
  const ua = navigator.userAgent || ''
  // Detectar Android e variantes (Samsung Internet, MIUI, etc.) que ocultam UA.
  if (/Android/i.test(ua)) return true
  if (/SamsungBrowser|Mi Browser|MiuiBrowser|HuaweiBrowser|HeyTapBrowser/i.test(ua)) return true
  return false
}
function isMobile() {
  if (isIos() || isAndroid()) return true
  // Fallback: touch + small screen (cobre Android com UA mascarado)
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  const smallScreen = Math.min(window.innerWidth || 0, window.innerHeight || 0) < 768
  return hasTouch && smallScreen
}
function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
}

// Botao "Instalar app" pequeno, pensado para o rodape (estilo igual aos outros botoes da footer)
export default function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [installed, setInstalled] = useState(isStandalone())

  useEffect(() => {
    if (installed) return
    const onBeforeInstall = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
    }
    const onAppInstalled = () => { setInstalled(true); setDeferredPrompt(null); setHelpOpen(false) }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [installed])

  if (installed) return null

  const ios = isIos()
  // Se nao for iOS mas for mobile (Android, Samsung Browser, ou qualquer UA mascarado),
  // tratar como Android (instrucoes do menu do browser sao iguais).
  const android = !ios && (isAndroid() || isMobile())

  const onClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt()
        const choice = await deferredPrompt.userChoice
        if (choice.outcome === 'accepted') setDeferredPrompt(null)
      } catch (e) { setHelpOpen(true) }
      return
    }
    setHelpOpen(true)
  }

  return (
    <>
      {/* Botao inline para a footer (sem position:fixed) */}
      <button
        onClick={onClick}
        title="Instalar LogiStock como app no telemovel ou computador"
        style={{
          background: 'rgba(255,255,255,0.18)',
          border: '1px solid rgba(255,255,255,0.35)',
          color: '#fff',
          fontSize: '11px', fontWeight: '600',
          cursor: 'pointer',
          padding: '2px 8px',
          borderRadius: '10px',
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          lineHeight: 1,
        }}
      >
        <Download size={11} />
        Instalar app
      </button>

      {/* Modal de instrucoes — visual 3 passos numerados, linguagem simples */}
      {helpOpen && (() => {
        // Decidir os 3 passos com base no SO
        const passos = ios ? [
          { icone: <Share size={28} />, titulo: 'Toca no Partilhar', texto: 'É o ícone com a seta para cima, em baixo no Safari.' },
          { icone: '➕', titulo: 'Adicionar ao Ecrã Principal', texto: 'Desliza para baixo na lista e escolhe esta opção.' },
          { icone: '✅', titulo: 'Confirma', texto: 'O ícone LogiStock aparece no telemóvel como uma app.' },
        ] : android ? [
          { icone: <MoreVertical size={28} />, titulo: 'Toca no menu', texto: '3 pontos no canto superior direito do browser.' },
          { icone: '➕', titulo: 'Adicionar ao ecrã principal', texto: 'Em Samsung Internet pode ser "Adicionar página a → Ecrã principal".' },
          { icone: '✅', titulo: 'Confirma', texto: 'O ícone LogiStock aparece no telemóvel como uma app.' },
        ] : [
          { icone: '🔍', titulo: 'Procura o ícone de instalar', texto: 'Na barra de endereço, à direita do URL — pequeno ecrã com seta para baixo.' },
          { icone: <MoreVertical size={28} />, titulo: 'Ou abre o menu', texto: '3 pontos no canto superior direito → "Instalar LogiStock Pro…".' },
          { icone: '✅', titulo: 'Confirma', texto: 'Atalho aparece no ambiente de trabalho.' },
        ]
        const titulo = ios ? '📱 iPhone / iPad' : android ? '📱 Telemóvel Android' : '🖥️ Computador'
        return (
          <div onClick={(e) => { if (e.target === e.currentTarget) setHelpOpen(false) }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <div style={{ background: C.surface, borderRadius: '14px', maxWidth: '440px', width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.30)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
              {/* Header amarelo */}
              <div style={{ padding: '20px 22px 16px', background: '#FACC15', color: '#0a0a0a', position: 'sticky', top: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.14em', opacity: 0.7 }}>Instalar como App</p>
                  <h2 style={{ margin: '4px 0 0', fontSize: '18px', fontWeight: 800 }}>{titulo}</h2>
                </div>
                <button onClick={() => setHelpOpen(false)}
                  style={{ background: 'rgba(0,0,0,0.1)', border: 'none', cursor: 'pointer', color: '#0a0a0a', padding: '6px', borderRadius: '8px', display: 'flex' }}>
                  <X size={18} />
                </button>
              </div>

              {/* 3 passos com numero grande */}
              <div style={{ padding: '18px 22px' }}>
                {passos.map((p, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '14px',
                    padding: '14px 0',
                    borderBottom: i < 2 ? `1px solid ${C.border}` : 'none',
                  }}>
                    <div style={{
                      width: '40px', height: '40px', flexShrink: 0,
                      borderRadius: '50%', background: '#0a0a0a', color: '#FACC15',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 900, fontSize: '18px',
                    }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span style={{
                          width: '34px', height: '34px',
                          background: '#fafafa', border: `1px solid ${C.border}`, borderRadius: '8px',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '18px', color: '#0a0a0a', flexShrink: 0,
                        }}>{p.icone}</span>
                        <strong style={{ fontSize: '15px', color: C.text }}>{p.titulo}</strong>
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>{p.texto}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tip final */}
              <div style={{ padding: '12px 22px 18px' }}>
                <p style={{ margin: 0, padding: '10px 12px', background: '#fff8e1', border: `1px solid #f0d070`, borderRadius: '8px', fontSize: '12px', color: C.text, lineHeight: 1.5 }}>
                  💡 Depois de instalado abres pelo ícone (sem barra do browser), funciona offline e parece uma app nativa.
                </p>
                {android && (
                  <p style={{ margin: '8px 0 0', padding: '10px 12px', background: '#e0f2fe', border: `1px solid #7dd3fc`, borderRadius: '8px', fontSize: '11px', color: C.text, lineHeight: 1.5 }}>
                    ℹ️ Se abriste pelo WhatsApp, Facebook ou Instagram, esses browsers não permitem instalar. Cola <code style={{ fontFamily: 'monospace' }}>logicpro.lognow.pt</code> no <strong>Chrome</strong>.
                  </p>
                )}
                <button onClick={() => setHelpOpen(false)}
                  style={{
                    marginTop: '14px', width: '100%', height: '44px',
                    background: '#0a0a0a', color: '#fff', border: 'none', borderRadius: '8px',
                    fontSize: '14px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em',
                  }}>
                  Entendi
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </>
  )
}
